// src/hooks/useNotifications.js
import { useState, useEffect, useCallback } from 'react';
import {
    getFCMToken,
    onMessageListener,
    isNotificationSupported,
    isNotificationEnabled,
    requestNotificationPermission
} from '../firebase/firebase-config';
import { get, post, put } from '../helpers/apiHelper';
import useAuthStore from '../store/authStore';

export const useNotifications = () => {
    const { user } = useAuthStore();
    const [isSupported, setIsSupported] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);
    const [fcmToken, setFcmToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Verificar soporte y estado inicial
    useEffect(() => {
        try {
            // Verificar si las notificaciones están soportadas de forma segura
            const supported = isNotificationSupported();
            setIsSupported(supported);

            // Solo verificar si están habilitadas si están soportadas
            if (supported) {
                const enabled = isNotificationEnabled();
                setIsEnabled(enabled);
            } else {
                setIsEnabled(false);
            }

            // NO inicializar automáticamente - solo verificar estado
            // La inicialización se hará manualmente cuando se necesite
        } catch (error) {
            console.warn('Error verificando notificaciones:', error);
            setIsSupported(false);
            setIsEnabled(false);
        }
    }, [user]);

    // Inicializar notificaciones
    const initializeNotifications = useCallback(async () => {
        if (!user?.userId) return;

        try {
            setLoading(true);
            setError(null);

            const token = await getFCMToken(user.userId);
            setFcmToken(token);

            // Configurar listener para mensajes en primer plano
            onMessageListener()
                .then((payload) => {
                    console.log('Mensaje recibido:', payload);
                    // Aquí se puede manejar la notificación como se desee
                })
                .catch((err) => console.error('Error en listener de mensajes:', err));

        } catch (err) {
            setError(err.message);
            console.error('Error inicializando notificaciones:', err);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Solicitar permisos
    const requestPermission = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const granted = await requestNotificationPermission();
            setIsEnabled(granted);

            if (granted && user?.userId) {
                await initializeNotifications();
            }

            return granted;
        } catch (err) {
            setError(err.message);
            return false;
        } finally {
            setLoading(false);
        }
    }, [user, initializeNotifications]);

    // Inicializar manualmente las notificaciones
    const initializeNotificationsManually = useCallback(async () => {
        if (!user?.userId) {
            throw new Error('Usuario no autenticado');
        }

        try {
            setLoading(true);
            setError(null);

            const token = await getFCMToken(user.userId);
            setFcmToken(token);

            // Configurar listener para mensajes en primer plano
            onMessageListener()
                .then((payload) => {
                    console.log('Mensaje recibido:', payload);
                    // Aquí se puede manejar la notificación como se desee
                })
                .catch((err) => console.error('Error en listener de mensajes:', err));

            return token;
        } catch (err) {
            setError(err.message);
            console.error('Error inicializando notificaciones:', err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Enviar notificación
    const sendNotification = useCallback(async (notificationData) => {
        try {
            setLoading(true);
            setError(null);

            const response = await post('notifications/business', notificationData);
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Obtener estado de entrega
    const getDeliveryStatus = useCallback(async (channel, messageId) => {
        try {
            const response = await get(`notifications/status/${channel}/${messageId}`);
            return response;
        } catch (err) {
            console.error('Error obteniendo estado de entrega:', err);
            throw err;
        }
    }, []);

    // Obtener historial de notificaciones
    const getNotificationHistory = useCallback(async (marcaId, filters = {}) => {
        try {
            const queryParams = new URLSearchParams(filters).toString();
            const response = await get(`notifications/history/${marcaId}?${queryParams}`);
            return response;
        } catch (err) {
            console.error('Error obteniendo historial:', err);
            throw err;
        }
    }, []);

    // Reenviar notificación fallida
    const retryNotification = useCallback(async (messageId) => {
        try {
            setLoading(true);
            setError(null);

            const response = await post(`notifications/retry/${messageId}`);
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Obtener configuración de notificaciones
    const getNotificationConfig = useCallback(async (marcaId) => {
        try {
            const response = await get(`notification-config/${marcaId}`);
            return response;
        } catch (err) {
            console.error('Error obteniendo configuración:', err);
            throw err;
        }
    }, []);

    // Actualizar configuración de notificaciones
    const updateNotificationConfig = useCallback(async (marcaId, config) => {
        try {
            setLoading(true);
            setError(null);

            const response = await put(`notification-config/${marcaId}`, config);
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Obtener templates de notificaciones
    const getNotificationTemplates = useCallback(async () => {
        try {
            const response = await get('notification-templates');
            return response;
        } catch (err) {
            console.error('Error obteniendo templates:', err);
            throw err;
        }
    }, []);

    // Crear template personalizado
    const createNotificationTemplate = useCallback(async (template) => {
        try {
            setLoading(true);
            setError(null);

            const response = await post('notification-templates', template);
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    // Actualizar template
    const updateNotificationTemplate = useCallback(async (templateId, template) => {
        try {
            setLoading(true);
            setError(null);

            const response = await put(`notification-templates/${templateId}`, template);
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        // Estado
        isSupported,
        isEnabled,
        fcmToken,
        loading,
        error,

        // Métodos
        requestPermission,
        sendNotification,
        getDeliveryStatus,
        getNotificationHistory,
        retryNotification,
        getNotificationConfig,
        updateNotificationConfig,
        getNotificationTemplates,
        createNotificationTemplate,
        updateNotificationTemplate,

        // Utilidades
        initializeNotifications,
        initializeNotificationsManually
    };
}; 